import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Clock, Landmark } from "lucide-react";
import { fmtDate, daysLeft } from "@/lib/format";

const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

export default function CalendarView({ tenders, onOpen }) {
  const withDates = (tenders || []).filter((t) => t.date_limite);
  const byDay = {};
  withDates.forEach((t) => {
    const k = (t.date_limite || "").slice(0, 10);
    (byDay[k] = byDay[k] || []).push(t);
  });
  const sortedDates = withDates.map((t) => t.date_limite).sort();
  const first = sortedDates.length ? new Date(sortedDates[0]) : new Date();
  const [cursor, setCursor] = useState(new Date(first.getFullYear(), first.getMonth(), 1));
  const [selected, setSelected] = useState(null);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const monthName = cursor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const keyFor = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const shift = (n) => { setCursor(new Date(year, month + n, 1)); setSelected(null); };
  const list = selected
    ? (byDay[selected] || [])
    : withDates.slice().sort((a, b) => (a.date_limite || "").localeCompare(b.date_limite || ""));

  return (
    <div className="grid lg:grid-cols-[1fr_1.1fr] gap-6" data-testid="agenda-calendar">
      <div className="rounded-2xl bg-white border border-border p-5">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => shift(-1)} className="p-2 rounded-lg hover:bg-slate-100" data-testid="cal-prev"><ChevronLeft className="w-5 h-5" /></button>
          <span className="font-display font-bold text-slate-900 capitalize">{monthName}</span>
          <button onClick={() => shift(1)} className="p-2 rounded-lg hover:bg-slate-100" data-testid="cal-next"><ChevronRight className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-xs font-mono text-slate-400 mb-1">
          {WEEKDAYS.map((w) => <div key={w} className="py-1">{w}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const k = keyFor(d);
            const items = byDay[k];
            const isSel = selected === k;
            return (
              <button
                key={i}
                onClick={() => items && setSelected(isSel ? null : k)}
                disabled={!items}
                data-testid={items ? `cal-day-${k}` : undefined}
                className={`aspect-square rounded-lg text-sm flex flex-col items-center justify-center transition-colors ${
                  items ? (isSel ? "bg-primary text-white font-bold" : "bg-blue-50 text-primary font-semibold hover:bg-blue-100") : "text-slate-400"
                }`}
              >
                {d}
                {items && <span className={`mt-0.5 w-1.5 h-1.5 rounded-full ${isSel ? "bg-white" : "bg-primary"}`} />}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-slate-400 mt-3">{selected ? `Échéances du ${fmtDate(selected)}` : "Toutes les échéances à venir. Cliquez un jour pour filtrer."}</p>
      </div>

      <div className="space-y-3">
        {list.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">Aucune échéance ce mois-ci.</p>}
        {list.map((t) => {
          const dl = daysLeft(t.date_limite);
          return (
            <button key={t.idweb} onClick={() => onOpen(t.idweb)} data-testid={`agenda-item-${t.idweb}`}
              className="w-full text-left rounded-xl bg-white border border-border p-4 hover-lift">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-mono text-slate-500">{fmtDate(t.date_limite)}</span>
                {dl != null && dl >= 0 && <span className="text-xs font-mono text-amber-600 flex items-center gap-1"><Clock className="w-3 h-3" /> J-{dl}</span>}
              </div>
              <p className="font-semibold text-slate-900 text-sm leading-snug line-clamp-2">{t.objet}</p>
              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Landmark className="w-3.5 h-3.5" /> {t.acheteur}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
